import React, { createContext, useContext, useState, useEffect } from "react";

export const LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧", short: "EN" },
  { code: "es", name: "Español", flag: "🇪🇸", short: "ES" },
  { code: "de", name: "Deutsch", flag: "🇩🇪", short: "DE" },
  { code: "fr", name: "Français", flag: "🇫🇷", short: "FR" },
];

const TRANSLATIONS = {
  en: {
    // Nav
    nav_find_jobs: "Find Jobs",
    nav_customize_cv: "Customize CV",
    nav_how_it_works: "How It Works",
    nav_about: "About Us",
    nav_compare: "Compare",
    nav_pricing: "Pricing",
    nav_login: "Log In",
    nav_start_free: "Start Free",
    nav_logout: "Log out",
    nav_dashboard: "Dashboard",
    nav_passport: "Career Profile",
    nav_ats: "ATS Checker",
    nav_customization: "CV Customization",
    nav_tracker: "Application Tracker",
    nav_interview: "Interview Copilot",
    nav_practice: "Face-to-Face",
    nav_recruiter_dash: "Dashboard",
    nav_candidate_search: "Candidate Search",
    nav_post_job: "Post Job",
    nav_pipeline: "Pipeline",

    // Shell
    header_recruiter_workspace: "Recruiter workspace",
    header_career_profile: "Your Career Profile",
    header_credits: "Credits",
    select_language: "Language",

    // Hero & Landing
    hero_badge: "Career, Evolved · From CV to interview",
    hero_title_1: "Let your CV speak for you.",
    hero_title_2: "CVOLVE helps you make it heard.",
    hero_sub: "Create a CV built specifically for the job you want. CVOLVE matches your experience with the Job Description, optimises your ATS score, prepares you with personalised interview questions and live interview practice, and helps you discover and apply for the right job opportunities, all in one platform.",
    hero_explore_jobs: "Explore Jobs",
    hero_customised_cv: "Customised CV",
    hero_cv_4_languages: "CV in 4 Languages",
    hero_ats_check: "ATS Check",
    hero_interview_practice: "Interview Practice",
    hero_job_search: "Job Search",

    // Sections
    sec_job_agg_label: "Global Job Aggregator",
    sec_job_agg_title: "Search Jobs on CVOLVE. Discover Opportunities Worldwide. Get Matched.",
    sec_job_agg_sub: "CVOLVE brings job opportunities from across the web into one place, so you can search for the roles you want, discover opportunities worldwide, and get matched with jobs aligned to your skills, experience and career goals.",
    sec_see_all_matches: "See all global matches",

    sec_ats_label: "ATS Intelligence",
    sec_ats_title: "CVOLVE Checks Your ATS Score and Gives You Actionable Insights.",
    sec_ats_sub: "Don’t just check your ATS score. CVOLVE shows you what’s working, what’s missing and where your CV can improve, from keywords and skills to experience, formatting and job description alignment.",
    sec_ats_overall: "Overall ATS Score",

    sec_custom_label: "Intelligent CV Customization",
    sec_custom_title: "Your Experience Is Unique. Your CV Should Be Too.",
    sec_custom_sub: "CVOLVE reads the job description, finds real gaps, asks you targeted questions — then rewrites your CV. Never fabricated.",

    sec_lang_label: "4-Language CV",
    sec_lang_title: "One Profile. Four Languages. Global Careers.",
    sec_lang_sub: "Generate a polished, native-quality CV in English, Spanish, German and French from the same Career Profile.",

    sec_interview_label: "Interview",
    sec_interview_title: "Know What They’ll Ask. Practice How You’ll Answer.",
    sec_interview_sub: "CVOLVE prepares you for the questions you’re likely to face, helps you structure stronger STAR answers, and lets you practise in a realistic face-to-face interview.",

    sec_autofill_label: "Application Autofill",
    sec_autofill_title: "Stop typing your career twice.",
    sec_autofill_sub: "CVOLVE completes long application forms from your Career Profile in seconds.",

    sec_ecosystem_label: "One Ecosystem",
    sec_ecosystem_title: "Talent and opportunity. One ecosystem.",
    sec_ecosystem_sub: "CVOLVE connects job seekers and recruiters through the same intelligent platform.",

    sec_compare_label: "Comparison",
    sec_compare_title: "Why use five platforms when your career can have one home?",
    sec_compare_sub: "CVOLVE replaces the job board, the network, the resume tool, the ATS checker and the interview app.",
    sec_see_full_comparison: "See full comparison",

    sec_pricing_label: "Pricing",
    sec_pricing_title: "Plans that evolve with you.",
    sec_pricing_sub: "Start free. Upgrade when your career demands more firepower.",

    // Pricing Cards
    tab_monthly_plans: "Monthly Plans",
    tab_credit_packs: "Credit Top-Up Packs",
    cta_start_free: "Start Free",
    cta_subscribe_career: "Subscribe to Career Pro",
    cta_subscribe_interview: "Subscribe to Interview Pro",
    most_popular: "Most Popular",
    recommended_pack: "Recommended Pack",
    credits_per_month: "Credits / month",
    credits_topup: "Credits",
    one_time: "one-time",

    // Career Profile
    profile_skills: "Skills",
    profile_edit: "Edit",
    profile_strength: "Profile strength",
    profile_role_objectives: "Core Role & Career Objectives",
    profile_experience: "Professional Experience",
    profile_education: "Education & Certifications",
    profile_export_pdf: "Export CV PDF",
    profile_upload_cv: "Upload CV",
    profile_save: "Save Changes",
    profile_cancel: "Cancel",

    // Common
    common_loading: "Loading...",
    common_apply: "Apply",
    common_search: "Search",
    common_filter: "Filter",
    common_match: "match",
  },

  es: {
    // Nav
    nav_find_jobs: "Buscar Empleos",
    nav_customize_cv: "Personalizar CV",
    nav_how_it_works: "Cómo Funciona",
    nav_about: "Sobre Nosotros",
    nav_compare: "Comparar",
    nav_pricing: "Precios",
    nav_login: "Iniciar Sesión",
    nav_start_free: "Comenzar Gratis",
    nav_logout: "Cerrar sesión",
    nav_dashboard: "Panel de Control",
    nav_passport: "Perfil Profesional",
    nav_ats: "Verificador ATS",
    nav_customization: "Personalización de CV",
    nav_tracker: "Seguimiento de Empleos",
    nav_interview: "Copiloto de Entrevistas",
    nav_practice: "Cara a Cara",
    nav_recruiter_dash: "Panel Reclutador",
    nav_candidate_search: "Buscar Candidatos",
    nav_post_job: "Publicar Empleo",
    nav_pipeline: "Proceso de Selección",

    // Shell
    header_recruiter_workspace: "Espacio de Reclutador",
    header_career_profile: "Tu Perfil Profesional",
    header_credits: "Créditos",
    select_language: "Idioma",

    // Hero & Landing
    hero_badge: "Carrera Evolucionada · Del CV a la entrevista",
    hero_title_1: "Deja que tu CV hable por ti.",
    hero_title_2: "CVOLVE te ayuda a hacerte escuchar.",
    hero_sub: "Crea un CV diseñado específicamente para el trabajo que deseas. CVOLVE compara tu experiencia con la descripción del puesto, optimiza tu puntuación ATS, te prepara con preguntas de entrevista personalizadas y práctica en vivo, y te ayuda a encontrar las mejores oportunidades.",
    hero_explore_jobs: "Explorar Empleos",
    hero_customised_cv: "CV Personalizado",
    hero_cv_4_languages: "CV en 4 Idiomas",
    hero_ats_check: "Verificación ATS",
    hero_interview_practice: "Práctica de Entrevistas",
    hero_job_search: "Búsqueda de Empleo",

    // Sections
    sec_job_agg_label: "Agregador Global de Empleos",
    sec_job_agg_title: "Busca Empleos en CVOLVE. Descubre Oportunidades Globales. Encuentra tu Match.",
    sec_job_agg_sub: "CVOLVE reúne ofertas de empleo de toda la web en un solo lugar para que encuentres roles alineados con tus habilidades y metas profesionales.",
    sec_see_all_matches: "Ver todas las ofertas globales",

    sec_ats_label: "Inteligencia ATS",
    sec_ats_title: "CVOLVE evalúa tu puntaje ATS y te brinda recomendaciones prácticas.",
    sec_ats_sub: "No te limites a ver tu puntuación. Descubre qué funciona, qué falta y cómo mejorar tus palabras clave, formato y alineación con la vacante.",
    sec_ats_overall: "Puntaje General ATS",

    sec_custom_label: "Personalización Inteligente de CV",
    sec_custom_title: "Tu experiencia es única. Tu CV también debería serlo.",
    sec_custom_sub: "CVOLVE analiza la vacante, detecta brechas reales, te hace preguntas clave y reescribe tu CV con total autenticidad.",

    sec_lang_label: "CV en 4 Idiomas",
    sec_lang_title: "Un Perfil. Cuatro Idiomas. Carreras Globales.",
    sec_lang_sub: "Genera un CV impecable y profesional en inglés, español, alemán y francés a partir del mismo perfil.",

    sec_interview_label: "Entrevista",
    sec_interview_title: "Anticipa las preguntas. Domina tus respuestas.",
    sec_interview_sub: "Prepárate para las preguntas más probables, estructura respuestas STAR de alto impacto y practica en una sala de simulación cara a cara.",

    sec_autofill_label: "Autocompletado de Formularios",
    sec_autofill_title: "Deja de escribir tu historial una y otra vez.",
    sec_autofill_sub: "CVOLVE rellena formularios de solicitud extensos a partir de tu Perfil Profesional en cuestión de segundos.",

    sec_ecosystem_label: "Un Solo Ecosistema",
    sec_ecosystem_title: "Talento y oportunidad. Un solo ecosistema.",
    sec_ecosystem_sub: "CVOLVE conecta candidatos y reclutadores a través de la misma plataforma inteligente.",

    sec_compare_label: "Comparativa",
    sec_compare_title: "¿Por qué usar cinco plataformas cuando tu carrera puede tener un solo hogar?",
    sec_compare_sub: "CVOLVE sustituye bolsas de trabajo, redes, editores de CV, analizadores ATS y apps de entrevista.",
    sec_see_full_comparison: "Ver comparativa completa",

    sec_pricing_label: "Precios",
    sec_pricing_title: "Planes que evolucionan contigo.",
    sec_pricing_sub: "Empieza gratis. Mejora cuando tu carrera requiera más potencia.",

    // Pricing Cards
    tab_monthly_plans: "Planes Mensuales",
    tab_credit_packs: "Paquetes de Créditos",
    cta_start_free: "Comenzar Gratis",
    cta_subscribe_career: "Suscribirse a Career Pro",
    cta_subscribe_interview: "Suscribirse a Interview Pro",
    most_popular: "Más Popular",
    recommended_pack: "Paquete Recomendado",
    credits_per_month: "Créditos / mes",
    credits_topup: "Créditos",
    one_time: "pago único",

    // Career Profile
    profile_skills: "Habilidades",
    profile_edit: "Editar",
    profile_strength: "Fuerza del perfil",
    profile_role_objectives: "Rol Principal y Objetivos",
    profile_experience: "Experiencia Profesional",
    profile_education: "Educación y Certificaciones",
    profile_export_pdf: "Exportar CV en PDF",
    profile_upload_cv: "Subir CV",
    profile_save: "Guardar Cambios",
    profile_cancel: "Cancelar",

    // Common
    common_loading: "Cargando...",
    common_apply: "Postular",
    common_search: "Buscar",
    common_filter: "Filtrar",
    common_match: "coincidencia",
  },

  de: {
    // Nav
    nav_find_jobs: "Jobs Finden",
    nav_customize_cv: "Lebenslauf Anpassen",
    nav_how_it_works: "Wie Es Funktioniert",
    nav_about: "Über Uns",
    nav_compare: "Vergleich",
    nav_pricing: "Preise",
    nav_login: "Anmelden",
    nav_start_free: "Kostenlos Starten",
    nav_logout: "Abmelden",
    nav_dashboard: "Dashboard",
    nav_passport: "Karriereprofil",
    nav_ats: "ATS-Prüfung",
    nav_customization: "CV-Anpassung",
    nav_tracker: "Bewerbungs-Tracker",
    nav_interview: "Interview-Copilot",
    nav_practice: "Face-to-Face",
    nav_recruiter_dash: "Recruiter Dashboard",
    nav_candidate_search: "Kandidatensuche",
    nav_post_job: "Job Veröffentlichen",
    nav_pipeline: "Pipeline",

    // Shell
    header_recruiter_workspace: "Recruiter-Arbeitsbereich",
    header_career_profile: "Dein Karriereprofil",
    header_credits: "Guthaben",
    select_language: "Sprache",

    // Hero & Landing
    hero_badge: "Karriere weiterentwickelt · Vom Lebenslauf bis zum Interview",
    hero_title_1: "Lass deinen Lebenslauf sprechen.",
    hero_title_2: "CVOLVE verschafft dir Gehör.",
    hero_sub: "Erstelle einen Lebenslauf, der perfekt auf deinen Wunschjob abgestimmt ist. CVOLVE gleicht deine Erfahrung mit der Stellenbeschreibung ab, optimiert deinen ATS-Score und bereitet dich mit gezielten Fragen und Live-Interviewübungen vor.",
    hero_explore_jobs: "Jobs Erkunden",
    hero_customised_cv: "Individueller Lebenslauf",
    hero_cv_4_languages: "Lebenslauf in 4 Sprachen",
    hero_ats_check: "ATS-Check",
    hero_interview_practice: "Interview-Training",
    hero_job_search: "Jobsuche",

    // Sections
    sec_job_agg_label: "Globaler Job-Aggregator",
    sec_job_agg_title: "Finde Jobs auf CVOLVE. Entdecke weltweite Chancen. Perfekter Match.",
    sec_job_agg_sub: "CVOLVE bündelt Jobangebote aus dem gesamten Web an einem Ort, damit du genau die Rollen findest, die zu deinen Fähigkeiten und Karrierezielen passen.",
    sec_see_all_matches: "Alle globalen Treffer anzeigen",

    sec_ats_label: "ATS-Intelligenz",
    sec_ats_title: "CVOLVE prüft deinen ATS-Score und liefert konkrete Handlungsempfehlungen.",
    sec_ats_sub: "Erkenne Stärken, Lücken und Verbesserungspotenziale bei Schlüsselwörtern, Fähigkeiten, Formatierung und Ausrichtung auf das Anforderungsprofil.",
    sec_ats_overall: "Gesamter ATS-Score",

    sec_custom_label: "Intelligente Lebenslauf-Anpassung",
    sec_custom_title: "Deine Erfahrung ist einzigartig. Dein Lebenslauf sollte es auch sein.",
    sec_custom_sub: "CVOLVE analysiert die Stellenanzeige, identifiziert echte Lücken, stellt gezielte Fragen und formuliert deinen Lebenslauf präzise um.",

    sec_lang_label: "Lebenslauf in 4 Sprachen",
    sec_lang_title: "Ein Profil. Vier Sprachen. Globale Karrieren.",
    sec_lang_sub: "Erstelle aus einem einzigen Karriereprofil erstklassige Lebensläufe auf Deutsch, Englisch, Spanisch und Französisch.",

    sec_interview_label: "Interview-Vorbereitung",
    sec_interview_title: "Wisse, was gefragt wird. Perfektioniere deine Antworten.",
    sec_interview_sub: "Bereite dich auf typische Fragen vor, strukturiere überzeugende STAR-Antworten und übe in realistischen Live-Gesprächen.",

    sec_autofill_label: "Bewerbungs-Autofill",
    sec_autofill_title: "Tippe deine Karriere nicht doppelt ein.",
    sec_autofill_sub: "CVOLVE füllt lange Bewerbungsformulare in Sekundenschnelle aus deinem Karriereprofil aus.",

    sec_ecosystem_label: "Ein Ökosystem",
    sec_ecosystem_title: "Talent und Chance. Ein ganzheitliches Ökosystem.",
    sec_ecosystem_sub: "CVOLVE verbindet Bewerber und Recruiter über dieselbe intelligente Plattform.",

    sec_compare_label: "Vergleich",
    sec_compare_title: "Warum fünf Plattformen nutzen, wenn deine Karriere ein Zuhause haben kann?",
    sec_compare_sub: "CVOLVE ersetzt Jobbörsen, Netzwerke, Lebenslauf-Editoren, ATS-Checker und Interview-Apps.",
    sec_see_full_comparison: "Vollständigen Vergleich ansehen",

    sec_pricing_label: "Preise",
    sec_pricing_title: "Pläne, die mit dir wachsen.",
    sec_pricing_sub: "Kostenlos starten. Erweitere nach Bedarf für maximale Durchschlagskraft.",

    // Pricing Cards
    tab_monthly_plans: "Monatspläne",
    tab_credit_packs: "Guthaben-Pakete",
    cta_start_free: "Kostenlos Starten",
    cta_subscribe_career: "Career Pro Abonnieren",
    cta_subscribe_interview: "Interview Pro Abonnieren",
    most_popular: "Sehr Beliebt",
    recommended_pack: "Empfohlenes Paket",
    credits_per_month: "Credits / Monat",
    credits_topup: "Credits",
    one_time: "Einmalig",

    // Career Profile
    profile_skills: "Fähigkeiten",
    profile_edit: "Bearbeiten",
    profile_strength: "Profilstärke",
    profile_role_objectives: "Kernrolle & Karriereziele",
    profile_experience: "Berufserfahrung",
    profile_education: "Ausbildung & Zertifikate",
    profile_export_pdf: "Lebenslauf als PDF exportieren",
    profile_upload_cv: "Lebenslauf hochladen",
    profile_save: "Änderungen speichern",
    profile_cancel: "Abbrechen",

    // Common
    common_loading: "Laden...",
    common_apply: "Bewerben",
    common_search: "Suchen",
    common_filter: "Filtern",
    common_match: "Übereinstimmung",
  },

  fr: {
    // Nav
    nav_find_jobs: "Trouver des Emplois",
    nav_customize_cv: "Personnaliser CV",
    nav_how_it_works: "Comment ça Marche",
    nav_about: "À Propos",
    nav_compare: "Comparer",
    nav_pricing: "Tarifs",
    nav_login: "Connexion",
    nav_start_free: "Commencer Gratuitement",
    nav_logout: "Se déconnecter",
    nav_dashboard: "Tableau de Bord",
    nav_passport: "Profil de Carrière",
    nav_ats: "Vérificateur ATS",
    nav_customization: "Personnalisation du CV",
    nav_tracker: "Suivi des Candidatures",
    nav_interview: "Copilote d'Entretien",
    nav_practice: "Face-à-Face",
    nav_recruiter_dash: "Tableau Recruteur",
    nav_candidate_search: "Recherche Candidats",
    nav_post_job: "Publier une Offre",
    nav_pipeline: "Pipeline",

    // Shell
    header_recruiter_workspace: "Espace Recruteur",
    header_career_profile: "Votre Profil de Carrière",
    header_credits: "Crédits",
    select_language: "Langue",

    // Hero & Landing
    hero_badge: "Carrière, Évoluée · Du CV à l'entretien",
    hero_title_1: "Laissez votre CV parler pour vous.",
    hero_title_2: "CVOLVE vous aide à vous faire entendre.",
    hero_sub: "Créez un CV conçu spécialement pour le poste visé. CVOLVE adapte votre expérience à l'offre d'emploi, optimise votre score ATS, vous entraîne avec des questions d'entretien sur mesure et vous connecte aux meilleures opportunités.",
    hero_explore_jobs: "Explorer les Offres",
    hero_customised_cv: "CV Personnalisé",
    hero_cv_4_languages: "CV en 4 Langues",
    hero_ats_check: "Vérification ATS",
    hero_interview_practice: "Pratique d'Entretien",
    hero_job_search: "Recherche d'Emploi",

    // Sections
    sec_job_agg_label: "Agrégateur Global d'Emplois",
    sec_job_agg_title: "Recherchez sur CVOLVE. Découvrez des Opportunités Mondiales. Soyez Matché.",
    sec_job_agg_sub: "CVOLVE rassemble les opportunités d'emploi du web entier au même endroit pour trouver les postes qui correspondent parfaitement à vos compétences et ambitions.",
    sec_see_all_matches: "Voir toutes les offres mondiales",

    sec_ats_label: "Intelligence ATS",
    sec_ats_title: "CVOLVE analyse votre score ATS et vous livre des recommandations concrètes.",
    sec_ats_sub: "Ne vous contentez pas d'un simple score. CVOLVE vous indique les forces, les lacunes et les points d'amélioration clés pour réussir la sélection des recruteurs.",
    sec_ats_overall: "Score ATS Global",

    sec_custom_label: "Personnalisation Intelligente de CV",
    sec_custom_title: "Votre expérience est unique. Votre CV doit l'être aussi.",
    sec_custom_sub: "CVOLVE lit l'offre d'emploi, identifie les besoins réels, vous pose des questions ciblées et reformule fidèlement votre CV.",

    sec_lang_label: "CV en 4 Langues",
    sec_lang_title: "Un Profil. Quatre Langues. Carrières Internationales.",
    sec_lang_sub: "Générez un CV soigné et professionnel en français, anglais, espagnol et allemand à partir du même profil.",

    sec_interview_label: "Entretien",
    sec_interview_title: "Anticipez les questions. Maîtrisez vos réponses.",
    sec_interview_sub: "Préparez-vous aux questions probables, structurez des réponses STAR percutantes et entraînez-vous en conditions réelles en face-à-face.",

    sec_autofill_label: "Remplissage Automatique",
    sec_autofill_title: "Ne retapez plus jamais votre parcours.",
    sec_autofill_sub: "CVOLVE remplit instantanément les longs formulaires de candidature à partir de votre Profil de Carrière.",

    sec_ecosystem_label: "Un Écosystème Unique",
    sec_ecosystem_title: "Talent et opportunité. Un écosystème unique.",
    sec_ecosystem_sub: "CVOLVE réunit candidats et recruteurs sur une seule et même plateforme intelligente.",

    sec_compare_label: "Comparatif",
    sec_compare_title: "Pourquoi utiliser 5 outils quand votre carrière peut avoir un seul foyer ?",
    sec_compare_sub: "CVOLVE remplace les sites d'emploi, les réseaux, les générateurs de CV, les vérificateurs ATS et les simulateurs d'entretien.",
    sec_see_full_comparison: "Voir le comparatif complet",

    sec_pricing_label: "Tarifs",
    sec_pricing_title: "Des formules qui grandissent avec vous.",
    sec_pricing_sub: "Commencez gratuitement. Passez au niveau supérieur lorsque votre carrière l'exige.",

    // Pricing Cards
    tab_monthly_plans: "Forfaits Mensuels",
    tab_credit_packs: "Packs de Crédits",
    cta_start_free: "Commencer Gratuitement",
    cta_subscribe_career: "S'abonner à Career Pro",
    cta_subscribe_interview: "S'abonner à Interview Pro",
    most_popular: "Le Plus Populaire",
    recommended_pack: "Pack Recommandé",
    credits_per_month: "Crédits / mois",
    credits_topup: "Crédits",
    one_time: "Paiement unique",

    // Career Profile
    profile_skills: "Compétences",
    profile_edit: "Modifier",
    profile_strength: "Force du profil",
    profile_role_objectives: "Poste Actuel & Objectifs",
    profile_experience: "Expérience Professionnelle",
    profile_education: "Formation & Diplômes",
    profile_export_pdf: "Exporter le CV en PDF",
    profile_upload_cv: "Importer un CV",
    profile_save: "Enregistrer les modifications",
    profile_cancel: "Annuler",

    // Common
    common_loading: "Chargement...",
    common_apply: "Postuler",
    common_search: "Rechercher",
    common_filter: "Filtrer",
    common_match: "compatibilité",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem("cvolve_portal_lang") || "en";
  });

  const setLanguage = (langCode) => {
    if (TRANSLATIONS[langCode]) {
      setLanguageState(langCode);
      localStorage.setItem("cvolve_portal_lang", langCode);
      document.documentElement.lang = langCode;
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (key, fallback = "") => {
    return TRANSLATIONS[language]?.[key] ?? TRANSLATIONS.en[key] ?? fallback ?? key;
  };

  const currentLang = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages: LANGUAGES, currentLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    return {
      language: "en",
      setLanguage: () => {},
      t: (k, def = "") => def || k,
      languages: LANGUAGES,
      currentLang: LANGUAGES[0],
    };
  }
  return ctx;
}
